import { motion } from "framer-motion";
import { Award, Globe2, BadgeCheck } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/animations";

const credentials = [
  { icon: BadgeCheck, label: "PMP Certified", sub: "Project Management Institute" },
  { icon: Award, label: "Customer Hero", sub: "Emerson Award" },
  { icon: Globe2, label: "Global Experience", sub: "Germany · Italy · UK · Spain · Singapore · Brazil · India · USA · France" },
];

export const About = () => {
  return (
    <section id="about" className="relative py-24 md:py-32 bg-[#051024]" data-testid="about-section">
      <div className="px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* Portrait */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full border border-[#D4AF37]" aria-hidden />
            <div className="relative overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_pmo-excellence/artifacts/h937qx8r_545e199f-6d74-4340-952b-986f49e1b902.jpg"
                alt="Deepshikha Jaiswal — PMP, PMO Consultant"
                className="w-full aspect-[4/5] object-cover object-top"
                data-testid="about-portrait"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#051024]/70 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] text-[#051024] px-6 py-4 hidden md:block">
              <p className="font-serif text-3xl leading-none">13+</p>
              <p className="text-[10px] uppercase tracking-[0.25em] mt-1">Years of Impact</p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="lg:col-span-7"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-4 mb-6">
            <div className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">About</span>
          </motion.div>

          <motion.h2
            variants={fadeUp}
            className="font-serif text-4xl md:text-5xl text-[#F8F9FA] leading-tight text-balance"
            data-testid="about-heading"
          >
            A career built at the intersection of <em className="text-[#D4AF37] font-medium">engineering</em> and{" "}
            <em className="text-[#D4AF37] font-medium">execution.</em>
          </motion.h2>

          <motion.p variants={fadeUp} className="mt-8 text-[#A8B2D1] text-lg leading-relaxed">
            Over thirteen years, Deepshikha has led project management offices and end-to-end delivery programs for
            global leaders in Oil &amp; Gas, Power, EPC, and Industrial Engineering. Her practice combines PMP-grade
            discipline with deep operational empathy — turning chaotic RFQ-to-order pipelines into reliable systems
            that scale.
          </motion.p>

          <motion.p variants={fadeUp} className="mt-5 text-[#A8B2D1] text-lg leading-relaxed">
            From establishing PMOs from scratch to standardizing quoting tools that cut response times by 60%, she
            has delivered measurable outcomes across teams in Germany, Italy, the UK, Spain, Singapore, Brazil, India,
            the United States, and France.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 grid sm:grid-cols-3 gap-5">
            {credentials.map((c) => (
              <div
                key={c.label}
                className="border border-[#233554] bg-[#0A192F] p-5 hover:border-[#D4AF37]/60 transition-colors"
                data-testid={`credential-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <c.icon size={22} className="text-[#D4AF37]" strokeWidth={1.5} />
                <p className="mt-3 font-serif text-lg text-[#F8F9FA]">{c.label}</p>
                <p className="text-xs text-[#A8B2D1] mt-1 leading-relaxed">{c.sub}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
